import React, { useState, useRef, useEffect } from 'react';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse, getQueryParamNames } from '../../../util/search';
import { parse, stringify } from '../../../util/urlHelpers';
import { createResourceLocatorString, matchPathname, pathByRouteName } from '../../../util/routes';
import {
  Button,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
} from '../../../components';
import { DownOutlined, RightOutlined } from '@ant-design/icons';

import MenuIcon from './MenuIcon';
import SearchIcon from './SearchIcon';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import FilterComponent from '../../SearchPage/FilterComponent';
import {
  groupListingFieldConfigs,
  initialValues,
  validUrlQueryParamsFromProps,
  pickListingFieldFilters,
} from '../../SearchPage/SearchPage.shared';

import css from './Topbar.module.css';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

const SEARCH_DISPLAY_ALWAYS = 'always';
const SEARCH_DISPLAY_NOT_LANDING_PAGE = 'notLandingPage';
const SEARCH_DISPLAY_ONLY_SEARCH_PAGE = 'onlySearchPage';

const redirectToURLWithModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...linkConfig,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return (
    <div className={classes}>
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  );
};

const TopbarComponent = props => {
  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentPage,
    notificationCount = 0,
    intl,
    history,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    routeConfiguration,
    isLandingPage,
  } = props;

  const handleSubmit = values => {
    const { currentSearchParams, history, config, routeConfiguration } = props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };
    
    // Stay on current page (LandingPage) if we're on it, otherwise go to SearchPage
    const targetRoute = resolvedCurrentPage === 'LandingPage' ? 'LandingPage' : 'SearchPage';
    history.push(createResourceLocatorString(targetRoute, routeConfiguration, {}, searchParams));
  };

  const handleLogout = () => {
    const { onLogout, history, routeConfiguration } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  };

  const { mobilemenu, mobilesearch, keywords, address, origin, bounds } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // Custom links are sorted so that group="primary" are always at the beginning of the list.
  const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
  const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
  const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

  const mobileMenu = (
    <TopbarMobileMenu
      isAuthenticated={isAuthenticated}
      currentUserHasListings={currentUserHasListings}
      currentUser={currentUser}
      onLogout={handleLogout}
      notificationCount={notificationCount}
      currentPage={resolvedCurrentPage}
      customLinks={customLinks}
    />
  );

  const topbarSearcInitialValues = () => {
    if (isMainSearchTypeKeywords(config)) {
      return { keywords };
    }

    // Only render current search if full place object is available in the URL params
    const locationFieldsPresent = isOriginInUse(config)
      ? address && origin && bounds
      : address && bounds;
    return {
      location: locationFieldsPresent
        ? {
            search: address,
            selectedPlace: { address, origin, bounds },
          }
        : null,
    };
  };
  const initialSearchFormValues = topbarSearcInitialValues();

  const classes = classNames(rootClassName || css.root, className);

  const { display: searchFormDisplay = SEARCH_DISPLAY_ALWAYS } = config?.topbar?.searchBar || {};

  // Search form is shown conditionally depending on configuration and
  // the current page.
  const showSearchOnAllPages = searchFormDisplay === SEARCH_DISPLAY_ALWAYS;
  const showSearchOnSearchPage =
    searchFormDisplay === SEARCH_DISPLAY_ONLY_SEARCH_PAGE && resolvedCurrentPage === 'SearchPage';
  const showSearchNotOnLandingPage =
    searchFormDisplay === SEARCH_DISPLAY_NOT_LANDING_PAGE && resolvedCurrentPage !== 'LandingPage';

  const showSearchForm =
    showSearchOnAllPages || showSearchOnSearchPage || showSearchNotOnLandingPage;

  const mobileSearchButtonMaybe = showSearchForm ? (
    <Button
      rootClassName={css.searchMenu}
      onClick={() => redirectToURLWithModalState(history, location, 'mobilesearch')}
      title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
    >
      <SearchIcon className={css.searchMenuIcon} />
    </Button>
  ) : (
    <div className={css.searchMenu} />
  );
  const category = config?.categoryConfiguration?.categories;
  console.log(config);

  const isSearchPage = resolvedCurrentPage === 'SearchPage';

  const CategoryFilter = ({ categories = [] }) => {
    // Simplified state management - only track open dropdowns and selected category
    const [openDropdowns, setOpenDropdowns] = useState({}); // Track multiple open dropdowns by path
    const [selectedCategoryId, setSelectedCategoryId] = useState(null); // Controls which category is currently selected
    const dropdownRef = useRef(null);
    const timeoutRefs = useRef({}); // Track timeouts for cleanup

    // Default icons for different category levels
    const getDefaultIcon = (category, level = 0) => {
      if (category.icon) return category.icon;

      // Default icons based on level or category name patterns
      const defaultIcons = ['🏷️', '📂', '📄'];
      return defaultIcons[level] || '🏷️';
    };

    // Helper to extract category ID and level from URL
    const getSelectedCategoryFromURL = () => {
      const queryParams = parse(location.search);
      if (queryParams.pub_categoryLevel3) {
        return queryParams.pub_categoryLevel3;
      }
      if (queryParams.pub_categoryLevel2) {
        return queryParams.pub_categoryLevel2;
      }
      if (queryParams.pub_categoryLevel1) {
        return queryParams.pub_categoryLevel1;
      }
      return null;
    };

    // Helper to find category and its ancestors by ID
    const findCategoryHierarchy = (categoryId, categoriesArray = categories) => {
      for (const category of categoriesArray) {
        if (category.id === categoryId) {
          return [category];
        }
        if (category.subcategories?.length > 0) {
          const subcategoryPath = findCategoryHierarchy(categoryId, category.subcategories);
          if (subcategoryPath.length > 0) {
            return [category, ...subcategoryPath];
          }
        }
      }
      return [];
    };

    // Helper to find category level (1, 2, or 3)
    const findCategoryLevel = (categoryId) => {
      const hierarchy = findCategoryHierarchy(categoryId);
      return hierarchy.length;
    };

    // Initialize selectedCategoryId from URL on component mount and location change
    useEffect(() => {
      setSelectedCategoryId(getSelectedCategoryFromURL());
    }, [location.search]);


    // Cleanup timeouts on unmount
    useEffect(() => {
      return () => {
        Object.values(timeoutRefs.current).forEach(clearTimeout);
      };
    }, []);

    useEffect(() => {
      const handleClickOutside = event => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setOpenDropdowns({});
          // Clear all timeouts when clicking outside
          Object.values(timeoutRefs.current).forEach(clearTimeout);
          timeoutRefs.current = {};
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    // Handle category selection and navigation
    const handleCategorySelection = (categoryId, categoryLevel = null, event = null) => {
      if (event) {
        event.stopPropagation(); // Prevent clicks from bubbling up
      }

      // If clicking the same category that's already selected, deselect it
      if (selectedCategoryId === categoryId) {
        handleClearSelection();
        return;
      }
      
      setSelectedCategoryId(categoryId);
      
      // Close all dropdowns after selection
      setOpenDropdowns({});
      // Clear all timeouts
      Object.values(timeoutRefs.current).forEach(clearTimeout);
      timeoutRefs.current = {};
      
      // Find the full category hierarchy (parent -> child -> grandchild)
      const hierarchy = findCategoryHierarchy(categoryId);
      const actualLevel = hierarchy.length;
      
      // Navigate based on current page - stay on landing page if already there
      const currentParams = parse(location.search);
      // Remove all existing category filters
      const newParams = pickBy(currentParams, (v, k) => !k.startsWith('pub_categoryLevel'));
      
      // Add all categories in the hierarchy to URL parameters
      hierarchy.forEach((category, index) => {
        const levelParam = `pub_categoryLevel${index + 1}`;
        newParams[levelParam] = category.id;
      });
      
      // Reset pagination when filtering
      delete newParams.page;
      
      // Stay on current page (LandingPage) if we're on it, otherwise go to SearchPage
      const targetRoute = resolvedCurrentPage === 'LandingPage' ? 'LandingPage' : 'SearchPage';
      history.push(createResourceLocatorString(targetRoute, routeConfiguration, {}, newParams));
    };

    // Handle clearing/removing category selection
    const handleClearSelection = (event = null) => {
      if (event) {
        event.stopPropagation(); // Prevent clicks from bubbling up
      }
      setSelectedCategoryId(null);
      
      // Close all dropdowns
      setOpenDropdowns({});
      // Clear all timeouts
      Object.values(timeoutRefs.current).forEach(clearTimeout);
      timeoutRefs.current = {};
      
      // Navigate based on current page - stay on landing page if already there
      const currentParams = parse(location.search);
      const newParams = pickBy(currentParams, (v, k) => !k.startsWith('pub_categoryLevel'));
      
      // Reset pagination when clearing filters
      delete newParams.page;
      
      // Stay on current page (LandingPage) if we're on it, otherwise go to SearchPage
      const targetRoute = resolvedCurrentPage === 'LandingPage' ? 'LandingPage' : 'SearchPage';
      history.push(createResourceLocatorString(targetRoute, routeConfiguration, {}, newParams));
    };

    // Helper to manage dropdown opening with timeout cleanup
    const openDropdown = (path, delay = 0) => {
      if (timeoutRefs.current[path]) {
        clearTimeout(timeoutRefs.current[path]);
        delete timeoutRefs.current[path];
      }
      
      if (delay > 0) {
        timeoutRefs.current[path] = setTimeout(() => {
          setOpenDropdowns(prev => ({ ...prev, [path]: true }));
          delete timeoutRefs.current[path];
        }, delay);
      } else {
        setOpenDropdowns(prev => ({ ...prev, [path]: true }));
      }
    };

    // Helper to manage dropdown closing with timeout cleanup
    const closeDropdown = (path, delay = 150) => {
      if (timeoutRefs.current[path]) {
        clearTimeout(timeoutRefs.current[path]);
      }
      
      timeoutRefs.current[path] = setTimeout(() => {
        setOpenDropdowns(prev => {
          const newState = { ...prev };
          delete newState[path];
          return newState;
        });
        delete timeoutRefs.current[path];
      }, delay);
    };

    // Helper to check if a category is in the selected hierarchy path
    const isInSelectedPath = (categoryId) => {
      if (!selectedCategoryId) return false;
      const selectedHierarchy = findCategoryHierarchy(selectedCategoryId);
      return selectedHierarchy.some(category => category.id === categoryId);
    };

    // Keyboard navigation handler
    const handleKeyDown = (event, categoryId, categoryLevel) => {
      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          handleCategorySelection(categoryId, categoryLevel, event);
          break;
        case 'Escape':
          setOpenDropdowns({});
          event.target.blur();
          break;
        case 'ArrowDown':
        case 'ArrowRight':
          event.preventDefault();
          // Focus next element logic would go here
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          event.preventDefault();
          // Focus previous element logic would go here
          break;
      }
    };

    return (
      <div className={css.categoryContainer} ref={dropdownRef}>
        <div className={css.categoryWrapper}>
          <div className={css.navigationContainer}>
            
            {categories.map(category => (
              <div key={category.id} style={{ position: 'relative' }}>
                <button
                  onClick={(e) => handleCategorySelection(category.id, 1, e)}
                  onMouseEnter={() => {
                    if (category.subcategories?.length > 0) {
                      openDropdown(`main-${category.id}`, 100);
                    }
                  }}
                  onMouseLeave={() => {
                    closeDropdown(`main-${category.id}`);
                  }}
                  onKeyDown={(e) => handleKeyDown(e, category.id, 1)}
                  className={`${css.categoryButton} ${
                    selectedCategoryId === category.id
                      ? css.categoryButtonSelected
                      : isInSelectedPath(category.id)
                      ? css.categoryButtonInPath
                      : openDropdowns[`main-${category.id}`]
                      ? css.categoryButtonActive
                      : css.categoryButtonInactive
                  }`}
                  aria-expanded={openDropdowns[`main-${category.id}`] || false}
                  aria-haspopup={category.subcategories?.length > 0}
                  aria-label={`${category.name} category${selectedCategoryId === category.id ? ' (selected)' : ''}`}
                >
                  <span className={css.categoryIcon} aria-hidden="true">{getDefaultIcon(category, 0)}</span>
                  <span>{category.name}</span>
                  {category.subcategories?.length > 0 && (
                    <DownOutlined
                      className={`${css.categoryChevron} ${
                        openDropdowns[`main-${category.id}`] ? css.categoryChevronRotated : ''
                      }`}
                      aria-hidden="true"
                    />
                  )}
                  {isInSelectedPath(category.id) && (
                    <button
                      className={css.categoryCloseButton}
                      onClick={(e) => handleClearSelection(e)}
                      title={`Clear category filter`}
                      aria-label={`Clear category filter`}
                    >
                      ✕
                    </button>
                  )}
                </button>

                {/* Mega Dropdown for first-level category */}
                {openDropdowns[`main-${category.id}`] && category.subcategories?.length > 0 && (
                  <div 
                    className={css.dropdown}
                    onMouseEnter={() => openDropdown(`main-${category.id}`)}
                    onMouseLeave={() => closeDropdown(`main-${category.id}`)}
                    role="menu"
                    aria-label={`${category.name} subcategories`}
                  >
                    <div className={css.dropdownContent}>
                      <div className={css.dropdownInner}>
                        {category.subcategories.map(subcategory => (
                          <div
                            key={subcategory.id}
                            className={css.subcategoryItem}
                            onMouseEnter={() => {
                              if (subcategory.subcategories?.length > 0) {
                                openDropdown(`sub-${category.id}-${subcategory.id}`, 100);
                              }
                            }}
                            onMouseLeave={() => {
                              closeDropdown(`sub-${category.id}-${subcategory.id}`);
                            }}
                          >
                            <button
                              className={`${css.subcategoryButton} ${
                                selectedCategoryId === subcategory.id
                                  ? css.subcategoryButtonSelected
                                  : isInSelectedPath(subcategory.id)
                                  ? css.subcategoryButtonInPath
                                  : openDropdowns[`sub-${category.id}-${subcategory.id}`]
                                  ? css.subcategoryButtonHover
                                  : css.subcategoryButtonNormal
                              }`}
                              onClick={(e) => handleCategorySelection(subcategory.id, 2, e)}
                              onKeyDown={(e) => handleKeyDown(e, subcategory.id, 2)}
                              role="menuitem"
                              aria-label={`${subcategory.name} subcategory${selectedCategoryId === subcategory.id ? ' (selected)' : ''}`}
                            >
                              <div className={css.subcategoryContent}>
                                <span className={css.subcategoryIcon} aria-hidden="true">
                                  {getDefaultIcon(subcategory, 1)}
                                </span>
                                <div>
                                  <div className={css.subcategoryText}>{subcategory.name}</div>
                                  {subcategory.subcategories?.length > 0 && (
                                    <div className={css.subcategoryCount}>
                                      {subcategory.subcategories.length} items
                                    </div>
                                  )}
                                </div>
                              </div>
                              {subcategory.subcategories?.length > 0 && (
                                <RightOutlined
                                  className={`${css.subcategoryChevron} ${
                                    openDropdowns[`sub-${category.id}-${subcategory.id}`]
                                      ? css.subcategoryChevronRotated
                                      : ''
                                  }`}
                                  aria-hidden="true"
                                />
                              )}
                              {isInSelectedPath(subcategory.id) && (
                                <button
                                  className={css.subcategoryCloseButton}
                                  onClick={(e) => handleClearSelection(e)}
                                  title={`Clear category filter`}
                                  aria-label={`Clear category filter`}
                                >
                                  ✕
                                </button>
                              )}
                            </button>

                            {/* Third Level Submenu for second-level subcategory */}
                            {openDropdowns[`sub-${category.id}-${subcategory.id}`] && subcategory.subcategories?.length > 0 && (
                              <div
                                className={css.thirdLevelDropdown}
                                onMouseEnter={() => openDropdown(`sub-${category.id}-${subcategory.id}`)}
                                onMouseLeave={() => closeDropdown(`sub-${category.id}-${subcategory.id}`)}
                                role="menu"
                                aria-label={`${subcategory.name} sub-subcategories`}
                              >
                                <div className={css.thirdLevelContent}>
                                  <div className={css.thirdLevelInner}>
                                    {subcategory.subcategories.map(deepSub => (
                                      <button 
                                        key={deepSub.id} 
                                        className={`${css.thirdLevelItem} ${
                                          selectedCategoryId === deepSub.id 
                                            ? css.thirdLevelItemSelected 
                                            : isInSelectedPath(deepSub.id)
                                            ? css.thirdLevelItemInPath
                                            : ''
                                        }`}
                                        onClick={(e) => handleCategorySelection(deepSub.id, 3, e)}
                                        onKeyDown={(e) => handleKeyDown(e, deepSub.id, 3)}
                                        role="menuitem"
                                        aria-label={`${deepSub.name} sub-subcategory${selectedCategoryId === deepSub.id ? ' (selected)' : ''}`}
                                        style={{ position: 'relative' }}
                                      >
                                        <span className={css.thirdLevelIcon} aria-hidden="true">
                                          {getDefaultIcon(deepSub, 2)}
                                        </span>
                                        <div className={css.thirdLevelText}>{deepSub.name}</div>
                                        {isInSelectedPath(deepSub.id) && (
                                          <button
                                            className={css.thirdLevelCloseButton}
                                            onClick={(e) => handleClearSelection(e)}
                                            title={`Clear category filter`}
                                            aria-label={`Clear category filter`}
                                          >
                                            ✕
                                          </button>
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const HorizontalFilters = ({ location, history, routeConfiguration }) => {
    const [currentQueryParams, setCurrentQueryParams] = useState(parse(location.search));
    
    // Add comprehensive null checks for config and its properties
    console.log('HorizontalFilters config check:', { config, categoryConfig: config?.categoryConfiguration });
    
    if (!config) {
      console.log('Config not available yet');
      return null;
    }
    
    if (!config.categoryConfiguration) {
      console.log('CategoryConfiguration not available yet');
      return null;
    }
    
    if (!config.listing) {
      console.log('Listing config not available yet');
      return null;
    }
    
    if (!config.search) {
      console.log('Search config not available yet');
      return null;
    }
    
    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes?.map(config => config.listingType) || [];
    const marketplaceCurrency = config?.currency;
    const categoryConfiguration = config?.categoryConfiguration;
    const listingCategories = categoryConfiguration?.categories || [];
    
    // Add try-catch around the function that might be causing the error
    let listingFieldsConfig = [];
    try {
      listingFieldsConfig = pickListingFieldFilters({
        listingFields: listingFields || [],
        locationSearch: location.search,
        categoryConfiguration,
      }) || [];
      console.log('pickListingFieldFilters succeeded:', listingFieldsConfig);
    } catch (error) {
      console.error('pickListingFieldFilters failed:', error);
      // Return empty component if this function fails
      return null;
    }

    // Filter out category filters since they're handled separately
    const builtInPrimaryFilters = defaultFiltersConfig?.filter(f => 
      !['categoryLevel', 'keywords'].includes(f.key) && f.group === 'primary'
    ) || [];
    
    const builtInSecondaryFilters = defaultFiltersConfig?.filter(f => 
      !['categoryLevel', 'keywords'].includes(f.key) && f.group === 'secondary'
    ) || [];
    
    // Add try-catch around groupListingFieldConfigs as well
    let customPrimaryFilters = [];
    let customSecondaryFilters = [];
    try {
      const groupedFilters = groupListingFieldConfigs(listingFieldsConfig, activeListingTypes) || [[], []];
      customPrimaryFilters = groupedFilters[0] || [];
      customSecondaryFilters = groupedFilters[1] || [];
      console.log('groupListingFieldConfigs succeeded:', { customPrimaryFilters, customSecondaryFilters });
    } catch (error) {
      console.error('groupListingFieldConfigs failed:', error);
      // Continue with empty arrays if this fails
      customPrimaryFilters = [];
      customSecondaryFilters = [];
    }
    
    // Only show primary filters in topbar for better UX
    const availableFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
    ];

    const urlQueryParams = parse(location.search);
    
    const getHandleChangedValueFn = () => {
      return (queryParamName, queryParamValue) => {
        const updatedURLParams = {
          ...urlQueryParams,
          [queryParamName]: queryParamValue,
        };
        
        // Reset pagination when filtering
        delete updatedURLParams.page;
        
        // Stay on current page (LandingPage) if we're on it, otherwise go to SearchPage
        const targetRoute = resolvedCurrentPage === 'LandingPage' ? 'LandingPage' : 'SearchPage';
        history.push(createResourceLocatorString(targetRoute, routeConfiguration, {}, updatedURLParams));
      };
    };

    if (availableFilters.length === 0) {
      return null;
    }

    return (
      <div className={css.horizontalFiltersContainer}>
        <div className={css.horizontalFiltersWrapper}>
          <div className={css.horizontalFiltersLabel}>
            <span>Filter by:</span>
          </div>
          <div className={css.horizontalFiltersRow}>
            {availableFilters.map(filterConfig => {
              const key = `TopbarFilters.${filterConfig.scope || 'built-in'}.${filterConfig.key}`;
              
              // Add try-catch around initialValues function call
              let filterInitialValues = {};
              try {
                filterInitialValues = initialValues({ location, config }, currentQueryParams) || {};
              } catch (error) {
                console.error('initialValues failed for filter:', filterConfig.key, error);
                filterInitialValues = {};
              }
              
              return (
                <FilterComponent
                  key={key}
                  idPrefix="TopbarFilters"
                  className={css.horizontalFilter}
                  config={filterConfig}
                  listingCategories={listingCategories}
                  marketplaceCurrency={marketplaceCurrency}
                  urlQueryParams={urlQueryParams}
                  initialValues={filterInitialValues}
                  getHandleChangedValueFn={getHandleChangedValueFn}
                  intl={intl}
                  liveEdit
                  showAsPopup={true}
                  isDesktop={true}
                />
              );
            })}
            {availableFilters.length > 0 && (
              <button 
                className={css.clearFiltersButton}
                onClick={() => {
                  const currentParams = parse(location.search);
                  const newParams = Object.keys(currentParams).reduce((acc, key) => {
                    // Keep location, bounds, and keywords but remove filter params
                    if (['address', 'bounds', 'keywords'].includes(key) || !key.startsWith('pub_')) {
                      acc[key] = currentParams[key];
                    }
                    return acc;
                  }, {});
                  
                  const targetRoute = resolvedCurrentPage === 'LandingPage' ? 'LandingPage' : 'SearchPage';
                  history.push(createResourceLocatorString(targetRoute, routeConfiguration, {}, newParams));
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={classes}>
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={resolvedCurrentPage}
      />
      <div className={classNames(mobileRootClassName || css.container, mobileClassName)}>
        <Button
          rootClassName={css.menu}
          onClick={() => redirectToURLWithModalState(history, location, 'mobilemenu')}
          title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
        >
          <MenuIcon className={css.menuIcon} />
          {notificationDot}
        </Button>
        <LinkedLogo
          layout={'mobile'}
          alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        {mobileSearchButtonMaybe}
      </div>
      <div className={css.desktop}>
        <TopbarDesktop
          className={desktopClassName}
          currentUserHasListings={currentUserHasListings}
          currentUser={currentUser}
          currentPage={resolvedCurrentPage}
          initialSearchFormValues={initialSearchFormValues}
          intl={intl}
          isAuthenticated={isAuthenticated}
          notificationCount={notificationCount}
          onLogout={handleLogout}
          onSearchSubmit={handleSubmit}
          config={config}
          customLinks={customLinks}
          showSearchForm={showSearchForm}
          // categoryFilter={isLandingPage ? <CategoryFilter categories={config?.categoryConfiguration?.categories || []} /> : null}
          // horizontalFilters={isSearchPage ? <HorizontalFilters location={location} history={history} routeConfiguration={routeConfiguration} /> : null}
        />
      </div>
      <Modal
        id="TopbarMobileMenu"
        containerClassName={css.modalContainer}
        isOpen={isMobileMenuOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilemenu')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        {authInProgress ? null : mobileMenu}
      </Modal>
      <Modal
        id="TopbarMobileSearch"
        containerClassName={css.modalContainerSearchForm}
        isOpen={isMobileSearchOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilesearch')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.searchContainer}>
          <TopbarSearchForm
            onSubmit={handleSubmit}
            initialValues={initialSearchFormValues}
            isMobile
            appConfig={config}
          />
          <p className={css.mobileHelp}>
            <FormattedMessage id="Topbar.mobileSearchHelp" />
          </p>
        </div>
      </Modal>
      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />

      <GenericError show={showGenericError} />
    </div>
  );
};

/**
 * Topbar containing logo, main search and navigation links.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.desktopClassName add more style rules for TopbarDesktop
 * @param {Object} props.mobileRootClassName overwrite mobile layout root classes
 * @param {Object} props.mobileClassName add more style rules for mobile layout
 * @param {boolean} props.isAuthenticated
 * @param {boolean} props.isLoggedInAs
 * @param {Object} props.currentUser
 * @param {boolean} props.currentUserHasListings
 * @param {boolean} props.currentUserHasOrders
 * @param {string} props.currentPage
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onManageDisableScrolling
 * @param {Function} props.onResendVerificationEmail
 * @param {Object} props.sendVerificationEmailInProgress
 * @param {Object} props.sendVerificationEmailError
 * @param {boolean} props.showGenericError
 * @param {Object} props.history
 * @param {Function} props.history.push
 * @param {Object} props.location
 * @param {string} props.location.search '?foo=bar'
 * @returns {JSX.Element} topbar component
 */
const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
